import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import InfoTooltip from './InfoTooltip';
import { PriceReqs, ZayavkaReqs, ProjectReqs } from './FileRequirements';

const LS_KEY = 'onboarding_collapsed';

const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-bold">{n}</span>
      <span className="text-sm font-semibold text-gray-900">{title}</span>
    </div>
    <p className="text-xs text-gray-600 leading-relaxed">{children}</p>
  </div>
);

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(LS_KEY) === '1');

  const toggle = () => setCollapsed((c) => {
    const n = !c;
    localStorage.setItem(LS_KEY, n ? '1' : '0');
    return n;
  });

  return (
    <div className="border-b border-gray-200 bg-white flex-shrink-0">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 sm:px-6 py-2.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">Как начать работу</span>
          <span className="text-xs text-gray-400">4 шага</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="hidden sm:inline">{collapsed ? 'показать' : 'свернуть'}</span>
          <ChevronDownIcon className={`h-5 w-5 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 sm:px-6 pb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <Step n={1} title="Загрузите прайс">
              Кнопка <b>«Загрузить прайс»</b> в левом нижнем углу. Excel-файл —{' '}
              <InfoTooltip label="требования" triggerClassName="text-emerald-700"><PriceReqs /></InfoTooltip>.
              Обрабатываем ~700 строк в минуту, обычно не дольше 20 минут.
            </Step>
            <Step n={2} title="Введите промокод">
              Пока грузится прайс —{' '}
              <button onClick={() => navigate('/billing')} className="text-emerald-700 underline underline-offset-2 hover:text-emerald-800">
                введите промокод
              </button>
              , если он у вас есть.
            </Step>
            <Step n={3} title="Загрузите заявку">
              В левой колонке — «Перетащите файлы или нажмите для выбора». Подойдёт{' '}
              <InfoTooltip label="что угодно: Excel, CSV, PDF, Word, фото" triggerClassName="text-emerald-700"><ZayavkaReqs /></InfoTooltip>
              , можно несколько файлов сразу. Отдельно про{' '}
              <InfoTooltip label="проект (PDF)" triggerClassName="text-emerald-700"><ProjectReqs /></InfoTooltip>.
            </Step>
            <Step n={4} title="Проверьте распознанное">
              Сначала появится <b>«Проверьте распознанное»</b> — что мы поняли из файла. Поправьте
              наименования и количества, удалите лишнее и нажмите «Всё верно». Поиск по прайсу
              запустится только после этого, а его итог — <b>«Результат анализа»</b>, из которого
              формируется счёт.
            </Step>
          </div>
          <p className="mt-3 text-xs text-gray-400 leading-relaxed">
            Что-то не загружается дольше 20 минут или система пишет «ошибка»? Напишите нам любым способом:{' '}
            <a href="mailto:mikirich@ya.ru" className="underline hover:text-gray-600">mikirich@ya.ru</a>,{' '}
            тел./MAX <a href="tel:+79120500500" className="underline hover:text-gray-600">+7 912 050-05-00</a>,{' '}
            Telegram <a href="https://t.me/kirichmi" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">@kirichmi</a>.
          </p>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
